/**
 * Lyrics Style Preview modal — Presets / Font / Effects tabs with a live
 * preview over the user's video (or the bundled Paper Boats sample). Presets
 * fill a full CaptionConfig; the Font and Effects tabs edit it. The resolved
 * config is persisted and burned into the exported video server-side, so a
 * style change is always a free re-export, never a re-render.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import {
  CAPTION_ANIMATION_OPTIONS,
  CAPTION_FONT_FAMILIES,
  CAPTION_FONTS_STYLESHEET_URL,
  CAPTION_SAMPLE_DURATION_SEC,
  CAPTION_SAMPLE_LYRICS,
  CAPTION_SAMPLE_VIDEO_URL,
  CAPTION_STYLE_PRESETS,
  CAPTION_WEIGHT_OPTIONS,
  cssForCaptionConfig,
  type CaptionAnimation,
  type CaptionConfig,
} from "@/lib/captionStyles";

const SAMPLE_LOOP_SEC = CAPTION_SAMPLE_DURATION_SEC;
const FONTS_LINK_ID = "solmi-caption-fonts";

export type LyricsStyleResult = {
  styleId: string;
  config: CaptionConfig | null;
};

function ensurePreviewFontsLoaded() {
  if (typeof document === "undefined") return;
  if (document.getElementById(FONTS_LINK_ID)) return;
  const link = document.createElement("link");
  link.id = FONTS_LINK_ID;
  link.rel = "stylesheet";
  link.href = CAPTION_FONTS_STYLESHEET_URL;
  document.head.appendChild(link);
}

const ANIMATION_KEYFRAMES = `
@keyframes capBounce { 0% { transform: scale(.4); } 55% { transform: scale(1.12); } 100% { transform: scale(1); } }
@keyframes capPop { 0% { transform: scale(0); } 100% { transform: scale(1); } }
@keyframes capScale { 0% { transform: scale(.82); } 100% { transform: scale(1.04); } }
@keyframes capSlideLeft { 0% { transform: translateX(90px); opacity: 0; } 100% { transform: translateX(0); opacity: 1; } }
@keyframes capSlideUp { 0% { transform: translateY(50px); opacity: 0; } 100% { transform: translateY(0); opacity: 1; } }
@keyframes lyricsStageDrift { from { transform: scale(1) translateY(0); } to { transform: scale(1.12) translateY(-3%); } }
`;

function lineAnimationCss(animation: CaptionAnimation): string | undefined {
  switch (animation) {
    case "bounce":
      return "capBounce .3s ease-out both";
    case "pop":
      return "capPop .16s ease-out both";
    case "scale":
      return "capScale 2s linear both";
    case "slide-left":
      return "capSlideLeft .24s ease-out both";
    case "slide-up":
      return "capSlideUp .24s ease-out both";
    default:
      return undefined;
  }
}

function CaptionOverlay({
  config,
  timeSec,
}: {
  config: CaptionConfig | null;
  timeSec: number;
}) {
  if (!config) return null;
  const active = CAPTION_SAMPLE_LYRICS.find(
    (line) => timeSec >= line.start && timeSec <= line.end,
  );
  if (!active) return null;
  const text = config.uppercase ? active.text.toUpperCase() : active.text;
  const words = text.split(/\s+/).filter(Boolean);
  const progress = Math.max(
    0,
    Math.min(1, (timeSec - active.start) / (active.end - active.start)),
  );
  const sungCount =
    config.animation === "karaoke"
      ? Math.floor(progress * words.length + 0.35)
      : 0;
  const vertical =
    config.position === "top"
      ? { top: "7%" }
      : config.position === "middle"
        ? { top: "50%", transform: "translateY(-50%)" }
        : // Matches the export's watermark-safe bottom margin (server
          // captionStyles.ts / watermarkLayout.ts): burned bottom captions
          // sit above the free-download badge band, so preview them there.
          { bottom: "23.5%" };
  return (
    <div
      style={{
        position: "absolute",
        left: "6%",
        right: "6%",
        display: "flex",
        justifyContent: "center",
        pointerEvents: "none",
        ...vertical,
      }}
    >
      <span
        key={`${active.start}-${config.animation}`}
        style={{
          ...cssForCaptionConfig(config, 0.55),
          maxWidth: "100%",
          display: "inline-block",
          animation: lineAnimationCss(config.animation),
        }}
      >
        {config.animation === "karaoke"
          ? words.map((word, i) => (
              <span
                key={i}
                style={{
                  color: i < sungCount ? config.highlightColor : undefined,
                  transition: "color .12s",
                }}
              >
                {word}
                {i < words.length - 1 ? " " : ""}
              </span>
            ))
          : text}
      </span>
    </div>
  );
}

/** Displayed video area inside the preview stage (object-fit: contain). */
export type StageRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

const HUD_GRAIN_BG =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")";

/**
 * Lookbook HUD preview (config.overlay === "hud"): mirrors the export burn in
 * server/captionStyles.ts buildHudAss — corner brackets, LOOK label, blinking
 * REC timecode, title, left-aligned lyric column — plus a vignette/grain hint
 * for the film texture. Sized to the displayed video, not the stage, so the
 * brackets sit on the frame's corners.
 */
export function HudOverlay({
  config,
  timeSec,
  title,
  rect,
  portrait,
}: {
  config: CaptionConfig;
  timeSec: number;
  title?: string | null;
  rect: StageRect | null;
  portrait: boolean;
}) {
  const box = rect ?? { left: 0, top: 0, width: 0, height: 0 };
  const s = rect ? rect.height / (portrait ? 1280 : 720) : 0.55;
  const m = 26 * s;
  const L = 46 * s;
  const T = Math.max(1.5, 3 * s);
  const edge = "rgba(255,255,255,0.85)";
  const hudText: React.CSSProperties = {
    position: "absolute",
    fontFamily: "'DejaVu Sans Mono', ui-monospace, Menlo, Consolas, monospace",
    fontWeight: 700,
    fontSize: Math.max(8, 17 * s),
    letterSpacing: s,
    color: "#fff",
    textShadow: "0 1px 2px rgba(0,0,0,0.6)",
    whiteSpace: "nowrap",
  };
  const look = Math.floor(timeSec / 15) + 1;
  const tc = new Date(Math.floor(timeSec) * 1000).toISOString().slice(11, 19);
  const active = CAPTION_SAMPLE_LYRICS.find(
    (line) => timeSec >= line.start && timeSec <= line.end,
  );
  const lyric = active
    ? config.uppercase
      ? active.text.toUpperCase()
      : active.text
    : null;
  const vertical: React.CSSProperties =
    config.position === "top"
      ? { top: "7%" }
      : config.position === "middle"
        ? { top: "50%", transform: "translateY(-50%)" }
        : { bottom: "23.5%" };
  const bracket = (pos: React.CSSProperties, sides: string[]) => (
    <span
      style={{
        position: "absolute",
        width: L,
        height: L,
        ...pos,
        ...Object.fromEntries(
          sides.map((side) => [`border${side}`, `${T}px solid ${edge}`]),
        ),
      }}
    />
  );
  return (
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        pointerEvents: "none",
        overflow: "hidden",
        ...(rect
          ? { left: box.left, top: box.top, width: box.width, height: box.height }
          : { inset: 0 }),
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.45) 100%)",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: HUD_GRAIN_BG,
          opacity: 0.16,
          mixBlendMode: "overlay",
        }}
      />
      {bracket({ left: m, top: m }, ["Top", "Left"])}
      {bracket({ right: m, top: m }, ["Top", "Right"])}
      {bracket({ left: m, bottom: m }, ["Bottom", "Left"])}
      {bracket({ right: m, bottom: m }, ["Bottom", "Right"])}
      <span style={{ ...hudText, left: m + 16 * s, top: m + 14 * s }}>
        LOOK {String(look).padStart(2, "0")}
      </span>
      <span style={{ ...hudText, left: m + 16 * s, top: m + 40 * s }}>
        <span
          style={{ color: "#FF3B30", opacity: timeSec % 1 < 0.55 ? 1 : 0 }}
        >
          ●
        </span>{" "}
        REC {tc}
      </span>
      <span style={{ ...hudText, right: m + 16 * s, top: m + 14 * s }}>
        {(title || "Your song").toUpperCase().slice(0, 32)}
      </span>
      {lyric && (
        <div
          style={{
            position: "absolute",
            left: (portrait ? 48 : 64) * s,
            right: portrait ? 48 * s : "36%",
            ...vertical,
          }}
        >
          <span
            key={`${active!.start}-${config.animation}`}
            style={{
              ...cssForCaptionConfig(config, s * (portrait ? 1.15 : 1)),
              display: "inline-block",
              maxWidth: "100%",
              animation: lineAnimationCss(config.animation),
            }}
          >
            {lyric}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── small controls ──────────────────────────────────────────────────────

const rowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
};
const labelStyle: React.CSSProperties = {
  fontSize: 12.5,
  color: "var(--fg-dim)",
  fontWeight: 600,
};
const inputStyle: React.CSSProperties = {
  background: "rgba(255,255,255,0.04)",
  border: "1px solid var(--line-2)",
  borderRadius: 8,
  color: "var(--fg)",
  fontSize: 12.5,
  fontFamily: "inherit",
  padding: "7px 9px",
};

function Toggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      style={{
        width: 38,
        height: 22,
        borderRadius: 999,
        border: "none",
        background: checked ? "#a78bfa" : "rgba(255,255,255,0.14)",
        position: "relative",
        cursor: "pointer",
        transition: "background .15s",
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 3,
          left: checked ? 19 : 3,
          width: 16,
          height: 16,
          borderRadius: 999,
          background: "#fff",
          transition: "left .15s",
        }}
      />
    </button>
  );
}

function ColorField({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <label
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        ...inputStyle,
        padding: "5px 9px",
        cursor: "pointer",
      }}
    >
      <input
        type="color"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        style={{
          width: 20,
          height: 20,
          border: "none",
          background: "transparent",
          padding: 0,
          cursor: "pointer",
        }}
      />
      <span style={{ fontSize: 11.5, color: "var(--fg-dim)" }}>
        {value.toUpperCase()}
      </span>
    </label>
  );
}

function NumberField({
  value,
  onChange,
  min,
  max,
  suffix,
  width = 74,
}: {
  value: number;
  onChange: (next: number) => void;
  min: number;
  max: number;
  suffix: string;
  width?: number;
}) {
  return (
    <span style={{ position: "relative", display: "inline-flex" }}>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(event) => {
          const num = Number(event.target.value);
          if (Number.isFinite(num)) onChange(Math.min(max, Math.max(min, num)));
        }}
        style={{ ...inputStyle, width, paddingRight: 26 }}
      />
      <span
        style={{
          position: "absolute",
          right: 8,
          top: "50%",
          transform: "translateY(-50%)",
          fontSize: 10.5,
          color: "var(--fg-mute)",
          pointerEvents: "none",
        }}
      >
        {suffix}
      </span>
    </span>
  );
}

// ─── modal ───────────────────────────────────────────────────────────────

export default function LyricsStyleModal({
  open,
  videoUrl,
  initialStyleId,
  initialConfig,
  onCancel,
  onConfirm,
  confirmLabel,
  hudTitle,
}: {
  open: boolean;
  /** The user's own video when one exists; else the bundled sample plays. */
  videoUrl?: string | null;
  initialStyleId?: string | null;
  initialConfig?: CaptionConfig | null;
  onCancel: () => void;
  onConfirm: (result: LyricsStyleResult) => void;
  confirmLabel?: string;
  /** Song title shown top-right in the Lookbook HUD preview. */
  hudTitle?: string | null;
}) {
  const [tab, setTab] = useState<"presets" | "font" | "effects">("presets");
  const [styleId, setStyleId] = useState<string>(initialStyleId || "none");
  const [config, setConfig] = useState<CaptionConfig | null>(
    initialConfig ?? null,
  );
  const [timeSec, setTimeSec] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const startRef = useRef<number>(0);

  useEffect(() => {
    if (!open) return;
    setTab("presets");
    // Keep an existing selection; otherwise open with Karaoke pre-selected
    // so the preview shows a real style instead of an empty frame.
    const hasExisting =
      !!initialConfig || (!!initialStyleId && initialStyleId !== "none");
    const effectiveId = hasExisting ? initialStyleId || "custom" : "karaoke";
    setStyleId(effectiveId);
    const presetConfig = CAPTION_STYLE_PRESETS.find(
      (p) => p.id === effectiveId,
    )?.config;
    setConfig(initialConfig ?? (presetConfig ? { ...presetConfig } : null));
    ensurePreviewFontsLoaded();
  }, [open, initialStyleId, initialConfig]);

  useEffect(() => {
    if (!open) return;
    let raf = 0;
    startRef.current = performance.now();
    const tick = () => {
      const video = videoRef.current;
      // While the video plays, captions follow its clock; when paused (or
      // not yet started) a wall-clock loop keeps the style preview cycling
      // over the still frame.
      if (video && !video.paused) {
        setTimeSec(video.currentTime % SAMPLE_LOOP_SEC);
      } else {
        setTimeSec(
          ((performance.now() - startRef.current) / 1000) % SAMPLE_LOOP_SEC,
        );
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [open]);

  // Editing any Font/Effects control detaches the config from its preset.
  const patchConfig = (patch: Partial<CaptionConfig>) => {
    setConfig((prev) => {
      const base =
        prev ??
        CAPTION_STYLE_PRESETS.find((p) => p.id === "karaoke")?.config ??
        null;
      if (!base) return prev;
      return { ...base, ...patch, preset: "custom" };
    });
    setStyleId("custom");
  };

  const previewSrc = useMemo(
    () => videoUrl || CAPTION_SAMPLE_VIDEO_URL,
    [videoUrl],
  );

  // Displayed video area (object-fit: contain) for the HUD preview.
  const stageRef = useRef<HTMLDivElement>(null);
  const [stageRect, setStageRect] = useState<StageRect | null>(null);
  const [videoPortrait, setVideoPortrait] = useState(false);
  useEffect(() => {
    if (!open) return;
    const stage = stageRef.current;
    const video = videoRef.current;
    if (!stage || !video) return;
    const measure = () => {
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      const sw = stage.clientWidth;
      const sh = stage.clientHeight;
      if (!vw || !vh || !sw || !sh) {
        setStageRect(null);
        return;
      }
      const fit = Math.min(sw / vw, sh / vh);
      const width = vw * fit;
      const height = vh * fit;
      setStageRect({
        left: (sw - width) / 2,
        top: (sh - height) / 2,
        width,
        height,
      });
      setVideoPortrait(vh > vw);
    };
    measure();
    video.addEventListener("loadedmetadata", measure);
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(measure)
        : null;
    observer?.observe(stage);
    return () => {
      video.removeEventListener("loadedmetadata", measure);
      observer?.disconnect();
    };
  }, [open, previewSrc]);

  if (!open || typeof document === "undefined") return null;

  const tabButton = (id: typeof tab, label: string) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      style={{
        padding: "7px 2px",
        border: "none",
        background: "transparent",
        color: tab === id ? "var(--fg)" : "var(--fg-mute)",
        fontSize: 13.5,
        fontWeight: 700,
        fontFamily: "inherit",
        cursor: "pointer",
        borderBottom:
          tab === id ? "2px solid #a78bfa" : "2px solid transparent",
      }}
    >
      {label}
    </button>
  );

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Lyrics style preview"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 300,
        background: "rgba(6,6,10,0.82)",
        backdropFilter: "blur(6px)",
        display: "grid",
        placeItems: "center",
        padding: 18,
      }}
      onClick={onCancel}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: "min(1460px, 100%)",
          maxHeight: "92vh",
          background: "#0d0d12",
          border: "1px solid var(--line-2)",
          borderRadius: 18,
          padding: 16,
          display: "grid",
          gap: 12,
        }}
      >
        <div style={rowStyle}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>
            Lyrics style preview
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onCancel}
            style={{
              display: "grid",
              placeItems: "center",
              width: 30,
              height: 30,
              borderRadius: 999,
              border: "1px solid var(--line-2)",
              background: "transparent",
              color: "var(--fg-dim)",
              cursor: "pointer",
            }}
          >
            <X size={15} />
          </button>
        </div>

        <div
          style={{
            display: "flex",
            gap: 14,
            alignItems: "stretch",
            height: "min(70vh, 660px)",
          }}
        >
          {/* Live preview with native player controls */}
          <div
            ref={stageRef}
            style={{
              position: "relative",
              flex: 1,
              minWidth: 0,
              borderRadius: 12,
              overflow: "hidden",
              background: "#000",
              border: "1px solid var(--line-2)",
            }}
          >
            <video
              ref={videoRef}
              src={previewSrc}
              controls
              loop
              playsInline
              preload="metadata"
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
            {config?.overlay === "hud" ? (
              <HudOverlay
                config={config}
                timeSec={timeSec}
                title={hudTitle}
                rect={stageRect}
                portrait={videoPortrait}
              />
            ) : (
              <CaptionOverlay config={config} timeSec={timeSec} />
            )}
            <style>{ANIMATION_KEYFRAMES}</style>
          </div>

          {/* Controls rail */}
          <div
            style={{
              width: 300,
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              gap: 10,
              minHeight: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 16,
                borderBottom: "1px solid var(--line-2)",
              }}
            >
              {tabButton("presets", "Presets")}
              {tabButton("font", "Font")}
              {tabButton("effects", "Effects")}
            </div>
            <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
              {tab === "presets" && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: 8,
                  }}
                >
                  {CAPTION_STYLE_PRESETS.map((item) => {
                    const active = styleId === item.id;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => {
                          setStyleId(item.id);
                          setConfig(item.config ? { ...item.config } : null);
                        }}
                        title={item.description}
                        style={{
                          display: "grid",
                          gap: 6,
                          justifyItems: "center",
                          padding: "12px 8px 9px",
                          borderRadius: 12,
                          border: active
                            ? "2px solid #a78bfa"
                            : "1px solid var(--line-2)",
                          background: active
                            ? "rgba(167,139,250,0.1)"
                            : "rgba(255,255,255,0.02)",
                          cursor: "pointer",
                          fontFamily: "inherit",
                        }}
                      >
                        <span
                          style={{
                            position: "relative",
                            display: "grid",
                            placeItems: "center",
                            width: "100%",
                            height: 46,
                            borderRadius: 8,
                            background: "#000",
                            overflow: "hidden",
                          }}
                        >
                          {item.config?.overlay === "hud" &&
                            (
                              [
                                ["Top", "Left"],
                                ["Top", "Right"],
                                ["Bottom", "Left"],
                                ["Bottom", "Right"],
                              ] as const
                            ).map(([v, h]) => (
                              <span
                                key={`${v}${h}`}
                                aria-hidden="true"
                                style={{
                                  position: "absolute",
                                  width: 7,
                                  height: 7,
                                  [v.toLowerCase()]: 4,
                                  [h.toLowerCase()]: 5,
                                  [`border${v}`]:
                                    "1.5px solid rgba(255,255,255,0.8)",
                                  [`border${h}`]:
                                    "1.5px solid rgba(255,255,255,0.8)",
                                }}
                              />
                            ))}
                          {item.config ? (
                            <span
                              style={{
                                ...cssForCaptionConfig(item.config, 0.32),
                                whiteSpace: "nowrap",
                                ...(item.config.overlay === "hud"
                                  ? { justifySelf: "start", paddingLeft: 14 }
                                  : null),
                              }}
                            >
                              {item.config.animation === "karaoke" ? (
                                <>
                                  <span
                                    style={{
                                      color: item.config.highlightColor,
                                    }}
                                  >
                                    {item.config.uppercase ? "SING " : "Sing "}
                                  </span>
                                  {item.config.uppercase ? "ALONG" : "along"}
                                </>
                              ) : item.config.uppercase ? (
                                "SING ALONG"
                              ) : (
                                "Sing along"
                              )}
                            </span>
                          ) : (
                            <span
                              style={{ color: "var(--fg-mute)", fontSize: 16 }}
                              aria-hidden="true"
                            >
                              ⊘
                            </span>
                          )}
                        </span>
                        <span
                          style={{
                            fontSize: 11.5,
                            fontWeight: 600,
                            color: active ? "var(--fg)" : "var(--fg-dim)",
                          }}
                        >
                          {item.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {tab === "font" && (
                <div style={{ display: "grid", gap: 11 }}>
                  <select
                    value={config?.fontFamily || "Montserrat"}
                    onChange={(event) =>
                      patchConfig({ fontFamily: event.target.value })
                    }
                    style={inputStyle}
                  >
                    {CAPTION_FONT_FAMILIES.map((family) => (
                      <option key={family} value={family}>
                        {family}
                      </option>
                    ))}
                  </select>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <ColorField
                      value={config?.color || "#ffffff"}
                      onChange={(color) => patchConfig({ color })}
                    />
                    <NumberField
                      value={config?.fontSizePx ?? 40}
                      min={12}
                      max={96}
                      suffix="px"
                      onChange={(fontSizePx) => patchConfig({ fontSizePx })}
                    />
                    <select
                      value={config?.fontWeight ?? 800}
                      onChange={(event) =>
                        patchConfig({
                          fontWeight: Number(
                            event.target.value,
                          ) as CaptionConfig["fontWeight"],
                        })
                      }
                      style={inputStyle}
                    >
                      {CAPTION_WEIGHT_OPTIONS.map((weight) => (
                        <option key={weight.value} value={weight.value}>
                          {weight.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={rowStyle}>
                    <span style={labelStyle}>Italic</span>
                    <Toggle
                      checked={!!config?.italic}
                      onChange={(italic) => patchConfig({ italic })}
                    />
                  </div>
                  <div style={rowStyle}>
                    <span style={labelStyle}>Underline</span>
                    <Toggle
                      checked={!!config?.underline}
                      onChange={(underline) => patchConfig({ underline })}
                    />
                  </div>
                  <div style={rowStyle}>
                    <span style={labelStyle}>Uppercase</span>
                    <Toggle
                      checked={!!config?.uppercase}
                      onChange={(uppercase) => patchConfig({ uppercase })}
                    />
                  </div>
                  <div style={rowStyle}>
                    <span style={labelStyle}>Font stroke</span>
                    <span
                      style={{
                        display: "inline-flex",
                        gap: 8,
                        alignItems: "center",
                      }}
                    >
                      {config?.strokeEnabled && (
                        <>
                          <ColorField
                            value={config?.strokeColor || "#000000"}
                            onChange={(strokeColor) =>
                              patchConfig({ strokeColor })
                            }
                          />
                          <NumberField
                            value={config?.strokeWidthPx ?? 6}
                            min={1}
                            max={20}
                            suffix="px"
                            onChange={(strokeWidthPx) =>
                              patchConfig({ strokeWidthPx })
                            }
                          />
                        </>
                      )}
                      <Toggle
                        checked={!!config?.strokeEnabled}
                        onChange={(strokeEnabled) =>
                          patchConfig({ strokeEnabled })
                        }
                      />
                    </span>
                  </div>
                  <div style={rowStyle}>
                    <span style={labelStyle}>Font shadows</span>
                    <Toggle
                      checked={!!config?.shadowEnabled}
                      onChange={(shadowEnabled) =>
                        patchConfig({ shadowEnabled })
                      }
                    />
                  </div>
                  {config?.shadowEnabled && (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <ColorField
                        value={config?.shadowColor || "#000000"}
                        onChange={(shadowColor) => patchConfig({ shadowColor })}
                      />
                      <NumberField
                        value={config?.shadowX ?? 2}
                        min={-12}
                        max={12}
                        suffix="x"
                        width={64}
                        onChange={(shadowX) => patchConfig({ shadowX })}
                      />
                      <NumberField
                        value={config?.shadowY ?? 2}
                        min={-12}
                        max={12}
                        suffix="y"
                        width={64}
                        onChange={(shadowY) => patchConfig({ shadowY })}
                      />
                      <NumberField
                        value={config?.shadowBlur ?? 2}
                        min={0}
                        max={24}
                        suffix="blur"
                        width={78}
                        onChange={(shadowBlur) => patchConfig({ shadowBlur })}
                      />
                    </div>
                  )}
                </div>
              )}

              {tab === "effects" && (
                <div style={{ display: "grid", gap: 11 }}>
                  <div style={{ display: "grid", gap: 6 }}>
                    <span style={labelStyle}>Position</span>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: 4,
                        background: "rgba(255,255,255,0.04)",
                        borderRadius: 10,
                        padding: 4,
                      }}
                    >
                      {(["top", "middle", "bottom"] as const).map(
                        (position) => (
                          <button
                            key={position}
                            type="button"
                            onClick={() => patchConfig({ position })}
                            style={{
                              padding: "7px 0",
                              borderRadius: 8,
                              border: "none",
                              background:
                                config?.position === position
                                  ? "#fff"
                                  : "transparent",
                              color:
                                config?.position === position
                                  ? "#0d0d12"
                                  : "var(--fg-dim)",
                              fontSize: 12.5,
                              fontWeight: 700,
                              fontFamily: "inherit",
                              cursor: "pointer",
                              textTransform: "capitalize",
                            }}
                          >
                            {position}
                          </button>
                        ),
                      )}
                    </div>
                  </div>
                  <div style={{ display: "grid", gap: 6 }}>
                    <span style={labelStyle}>Animation type</span>
                    <select
                      value={config?.animation || "none"}
                      onChange={(event) =>
                        patchConfig({
                          animation: event.target.value as CaptionAnimation,
                        })
                      }
                      style={inputStyle}
                    >
                      {CAPTION_ANIMATION_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={{ display: "grid", gap: 6 }}>
                    <span style={labelStyle}>Highlight color</span>
                    <ColorField
                      value={config?.highlightColor || "#a78bfa"}
                      onChange={(highlightColor) =>
                        patchConfig({ highlightColor })
                      }
                    />
                  </div>
                </div>
              )}
            </div>
            <div
              style={{
                fontSize: 11.5,
                lineHeight: 1.5,
                color: "rgba(255,255,255,0.55)",
              }}
            >
              Captions use the lyric lines from Music Analysis. Words missing
              or timing off? Fix them there (edit a line or Re-detect lyrics),
              then re-apply here — re-applying is always free.
            </div>
            <button
              type="button"
              onClick={() =>
                onConfirm({
                  styleId: config ? styleId : "none",
                  config,
                })
              }
              style={{
                padding: "11px 14px",
                borderRadius: 10,
                border: "none",
                background: "#fff",
                color: "#0d0d12",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              {confirmLabel || "Add Lyrics"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
